import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  Card,
  Divider,
  Grid,
  Group,
  Image,
  Loader,
  Modal,
  NumberInput,
  Paper,
  Skeleton,
  SimpleGrid,
  Stack,
  Table,
  Tabs,
  Text,
  TextInput,
  Textarea,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { Link, useParams } from 'react-router-dom';
import { getDeviceStatus } from '@utils';
import {
  Check,
  HeartPulse,
  History,
  Play,
  Plus,
  Ruler,
  SlidersHorizontal,
  X,
} from 'lucide-react';
import type { DeviceConfigurationApi } from '@model/DeviceConfiguration';
import type {
  BodyImageComparison,
  BodyImageEventName,
  BodyImageMeasurementSnapshot,
  BodyImageTrackingEvent,
  Device,
  DeviceConnectionStatus,
} from '@types';
import { useAbly } from '../../../../../providers/AblyProvider';
import { useSession } from '../../../../../hooks/useSession';
import { ConnectedDevices, SessionCharts } from '../../index';
import { SessionInfoCard } from '../../SessionInfoCard/SessionInfoCard';
import { SessionTimer } from '../../SessionTimer/SessionTimer';
import { SessionConfigSummary } from '../../../../../features/dev-tools';
import { useDebugEnabled } from '../../../../../utils/debugFlag';
import { PatientMeasurementFieldTile } from '../../../../../features/patients/components/PatientMeasurementFieldTile';
import { PatientMeasurementsForm } from '../../../../../features/patients/components/PatientMeasurementsForm';
import {
  createPatientMeasurement,
  fetchPatientById,
  fetchPatientMeasurementsHistory,
} from '../../../../../features/patients/services/patientsService';
import type {
  PatientBaseProfile,
  PatientMeasurementRecord,
  PatientRecord,
} from '../../../../../features/patients/types';
import {
  formatAgeValue,
  formatBiologicalSex,
  formatBirthYearValue,
  formatDateTimeValue,
  formatDateValue,
  formatDaysAgoLabel,
  formatMeasurementValue,
} from '../../../../../features/patients/utils/formatters';
import { createMeasurementPrefillFromLatest } from '../../../../../features/patients/utils/measurements';
import { deriveAgeFromDateOfBirth } from '../../../../../features/patients/utils/nutrition';
import {
  getMissingPatientBaseProfileLabels,
  isPatientBaseProfileComplete,
  resolvePatientBaseProfile,
} from '../../../../../features/patients/utils/profile';
import { patientsQueryKeys } from '../../../../../features/patients/hooks/usePatientsQueries';
import { USER_ROUTES } from '../../../../../routes/userRoutes';
import {
  BODY_IMAGE_METRIC_DEFINITIONS,
  BODY_IMAGE_MANNEQUIN_EVENT_NAME,
  buildBodyImageMannequinSelectPayload,
  buildBodyImageMannequinsComparePayload,
  buildBodyImageMannequinsHomePayload,
  buildBodyImageMannequinsRotatePayload,
  BODY_IMAGE_ROTATE_EVENT_NAME,
  buildBodyImageStartPayload,
  calculateBodyImageComparisonMetrics,
  extractBodyImageMeasurementSnapshot,
  getBodyImageFigurePlaceholderSrc,
  getBodyImageSelectedFigure,
  resolveBodyImageBiologicalSex,
  withBodyImageSelectedFigure,
} from '../../../../../features/body-image/utils';
import { BodyImageComparisonAccordionList } from '../../../../../features/body-image/BodyImageComparisonAccordionList';
import { formatBodyImageEventLabel } from '../../../../../features/body-image/eventMap';
import { BodyImageRotationCard } from '../../../../../features/body-image/BodyImageRotationCard';
import {
  createBodyImageComparison,
  deleteBodyImageComparison,
  createBodyImageTrackingEvent,
  deleteAllBodyImageTrackingEvents,
  fetchBodyImageComparisons,
  fetchBodyImageTrackingEvents,
} from '../../../../../services/bodyImageSessionManager';
import { updateConfiguration } from '../../../../../services/configurationManager';
const BODY_IMAGE_FIGURE_COUNT = 9;

interface BodyImageScenarioLayoutProps {
  session: DeviceConfigurationApi;
  appRequirements: { vr: boolean; sensor: boolean };
  allDevices: Device[];
  connectionStatus: Record<string, DeviceConnectionStatus>;
  onConfigureClick: () => void;
  isLoading: boolean;
}

type ComparisonDraftValues = {
  label: string;
  notes: string;
  values: Record<string, number | ''>;
};

const createEmptyComparisonDraft = (
  snapshot: BodyImageMeasurementSnapshot
): ComparisonDraftValues => ({
  label: '',
  notes: '',
  values: Object.fromEntries(
    BODY_IMAGE_METRIC_DEFINITIONS.map(definition => [
      definition.key,
      snapshot[definition.key] ?? '',
    ])
  ),
});

const statusColorByState: Record<string, string> = {
  queued: 'yellow',
  initializing: 'blue',
  'in-progress': 'green',
  completed: 'gray',
};

function SectionSkeletonList({
  items = 3,
  withActions = false,
}: {
  items?: number;
  withActions?: boolean;
}) {
  return (
    <Stack gap='sm'>
      {Array.from({ length: items }).map((_, index) => (
        <Paper key={index} withBorder p='sm' radius='sm'>
          <Stack gap='xs'>
            <Group justify='space-between' align='flex-start' wrap='nowrap'>
              <Skeleton h={14} w='42%' radius='xl' />
              {withActions ? (
                <Group gap={4} wrap='nowrap'>
                  <Skeleton h={24} w={24} radius='sm' />
                  <Skeleton h={24} w={24} radius='sm' />
                </Group>
              ) : null}
            </Group>
            <Skeleton h={10} w='92%' radius='xl' />
            <Skeleton h={10} w='76%' radius='xl' />
            <Skeleton h={8} w='28%' radius='xl' />
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
}

function TrackingTableSkeleton() {
  return (
    <Stack gap='xs'>
      <Group grow>
        <Skeleton h={12} radius='xl' />
        <Skeleton h={12} radius='xl' />
      </Group>
      {Array.from({ length: 4 }).map((_, index) => (
        <Group key={index} grow>
          <Skeleton h={32} radius='sm' />
          <Skeleton h={32} radius='sm' />
        </Group>
      ))}
    </Stack>
  );
}

function ActiveMeasurementPanelSkeleton() {
  return (
    <Stack gap='sm'>
      <Paper withBorder p='md' radius='sm'>
        <Stack gap='sm'>
          <Group justify='space-between' align='flex-start' wrap='wrap'>
            <Stack gap={6}>
              <Skeleton h={18} w={120} radius='xl' />
              <Skeleton h={12} w={280} radius='xl' />
            </Stack>
            <Skeleton h={14} w={110} radius='xl' />
          </Group>
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing='md'>
            {Array.from({ length: 3 }).map((_, index) => (
              <Stack key={index} gap={6}>
                <Skeleton h={12} w={90} radius='xl' />
                <Skeleton h={16} w={70} radius='xl' />
              </Stack>
            ))}
          </SimpleGrid>
        </Stack>
      </Paper>

      <Paper withBorder p='md' radius='sm'>
        <Stack gap='sm'>
          <Group
            justify='space-between'
            align='flex-start'
            wrap='wrap'
            gap='sm'
          >
            <Stack gap={6}>
              <Skeleton h={18} w={150} radius='xl' />
              <Skeleton h={12} w={130} radius='xl' />
            </Stack>
            <Group gap='xs' wrap='nowrap'>
              <Skeleton h={36} w={150} radius='sm' />
              <Skeleton h={36} w={130} radius='sm' />
            </Group>
          </Group>

          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing='sm'>
            {Array.from({ length: 6 }).map((_, index) => (
              <Skeleton key={index} h={52} radius='sm' />
            ))}
          </SimpleGrid>

          <Skeleton h={72} radius='sm' />
        </Stack>
      </Paper>

      <Group justify='center'>
        <Skeleton h={14} w={120} radius='xl' />
      </Group>
    </Stack>
  );
}

function getComparisonParameterValues(
  values: ComparisonDraftValues['values']
): BodyImageMeasurementSnapshot {
  return Object.fromEntries(
    Object.entries(values).filter(
      (entry): entry is [string, number] =>
        typeof entry[1] === 'number' && Number.isFinite(entry[1])
    )
  );
}

export const BodyImageScenarioLayout = React.memo<BodyImageScenarioLayoutProps>(
  ({
    session,
    appRequirements,
    allDevices,
    connectionStatus,
    onConfigureClick,
    isLoading,
  }) => {
    const { id } = useParams();
    const queryClient = useQueryClient();
    const { sendMessage } = useAbly();
    const { startExperience, terminateSession, cancelSession, isPending } =
      useSession(id);

    const [isMeasurementConfirmed, setIsMeasurementConfirmed] = useState(
      session.status !== 'queued'
    );
    const [isStartingSession, setIsStartingSession] = useState(false);
    const [comparisonDraft, setComparisonDraft] =
      useState<ComparisonDraftValues>(createEmptyComparisonDraft({}));

    const [
      comparisonModalOpened,
      { open: openComparisonModal, close: closeComparisonModal },
    ] = useDisclosure(false);
    const [
      measurementModalOpened,
      { open: openMeasurementModal, close: closeMeasurementModal },
    ] = useDisclosure(false);
    const [
      startConfirmModalOpened,
      { open: openStartConfirmModal, close: closeStartConfirmModal },
    ] = useDisclosure(false);
    const [workspaceTab, setWorkspaceTab] = useState<string | null>(
      session.status === 'queued' ? 'rilevazione' : 'controllo-eventi'
    );
    const [localTrackingEvents, setLocalTrackingEvents] = useState<
      BodyImageTrackingEvent[]
    >([]);
    const [savedProfileOverride, setSavedProfileOverride] =
      useState<PatientBaseProfile | null>(null);
    const [savedMeasurementOverride, setSavedMeasurementOverride] =
      useState<PatientMeasurementRecord | null>(null);
    const rotateStep = 90;
    const [rotationAngle, setRotationAngle] = useState(0);
    const [selectedFigure, setSelectedFigure] = useState<number | null>(() =>
      getBodyImageSelectedFigure(session.config)
    );
    const isDebugMode = useDebugEnabled();
    const [activeComparedComparisonId, setActiveComparedComparisonId] =
      useState<number | null>(null);
    const persistedSelectedFigure = useMemo(
      () => getBodyImageSelectedFigure(session.config),
      [session.config]
    );

    useEffect(() => {
      setLocalTrackingEvents([]);
      setRotationAngle(0);
      setActiveComparedComparisonId(null);
    }, [session.id]);

    useEffect(() => {
      setActiveComparedComparisonId(null);
    }, [selectedFigure]);

    useEffect(() => {
      setWorkspaceTab(
        session.status === 'queued' ? 'rilevazione' : 'controllo-eventi'
      );
    }, [session.id, session.status]);

    useEffect(() => {
      setSelectedFigure(persistedSelectedFigure);
    }, [session.id, persistedSelectedFigure]);

    const persistSelectedFigure = async (figure: number | null) => {
      const nextConfig = withBodyImageSelectedFigure(session.config, figure);
      const sessionQueryKey = ['session', String(session.id)] as const;
      const previousSession =
        queryClient.getQueryData<DeviceConfigurationApi>(sessionQueryKey);

      queryClient.setQueryData<DeviceConfigurationApi>(
        sessionQueryKey,
        current =>
          current == null
            ? current
            : {
                ...current,
                config: nextConfig as DeviceConfigurationApi['config'],
              }
      );

      try {
        await updateConfiguration(session.id, { config: nextConfig });
      } catch (error) {
        if (previousSession != null) {
          queryClient.setQueryData(sessionQueryKey, previousSession);
        }
        setSelectedFigure(getBodyImageSelectedFigure(previousSession?.config));
        console.error('[body-image] failed to persist selected figure', {
          sessionId: session.id,
          figure,
          error,
        });
        notifications.show({
          color: 'red',
          title: 'Salvataggio figura fallito',
          message:
            error instanceof Error
              ? error.message
              : 'Non è stato possibile salvare la figura selezionata.',
        });
        throw error;
      }
    };
    const measurementsQuery = useQuery({
      queryKey: ['body-image', 'patient-measurements', session.patient_id],
      queryFn: () => fetchPatientMeasurementsHistory(session.patient_id),
      enabled: session.patient_id > 0,
      placeholderData: keepPreviousData,
      refetchOnWindowFocus: false,
    });

    const patientQuery = useQuery({
      queryKey: ['body-image', 'patient-profile', session.patient_id],
      queryFn: async () => {
        const patient = await fetchPatientById(session.patient_id);
        const cached = queryClient.getQueryData<PatientRecord>([
          'body-image',
          'patient-profile',
          session.patient_id,
        ]);
        const cachedProfile = cached?.profile;
        const nextProfile = patient.profile;

        // Staging GET may omit profile fields; never wipe values we already have.
        return {
          ...patient,
          profile: {
            biologicalSex:
              nextProfile.biologicalSex ?? cachedProfile?.biologicalSex ?? null,
            dateOfBirth:
              nextProfile.dateOfBirth ?? cachedProfile?.dateOfBirth ?? null,
            heightCm: nextProfile.heightCm ?? cachedProfile?.heightCm ?? null,
          },
        } satisfies PatientRecord;
      },
      enabled: session.patient_id > 0,
      placeholderData: keepPreviousData,
      refetchOnWindowFocus: false,
    });

    useEffect(() => {
      setSavedProfileOverride(null);
      setSavedMeasurementOverride(null);
    }, [session.patient_id]);

    const latestMeasurementFromQuery = measurementsQuery.data?.[0] ?? null;
    const latestMeasurement = useMemo(() => {
      if (savedMeasurementOverride == null) {
        return latestMeasurementFromQuery;
      }

      if (latestMeasurementFromQuery == null) {
        return savedMeasurementOverride;
      }

      return (savedMeasurementOverride.id ?? 0) >=
        (latestMeasurementFromQuery.id ?? 0)
        ? savedMeasurementOverride
        : latestMeasurementFromQuery;
    }, [latestMeasurementFromQuery, savedMeasurementOverride]);
    const latestMeasurementRecordedAt =
      latestMeasurement?.createdAt ?? latestMeasurement?.updatedAt ?? null;
    const latestMeasurementDaysAgo = formatDaysAgoLabel(
      latestMeasurementRecordedAt
    );
    const registeredProfile = useMemo(() => {
      const fromPatient = patientQuery.data?.profile;

      return resolvePatientBaseProfile({
        biologicalSex:
          savedProfileOverride?.biologicalSex ??
          fromPatient?.biologicalSex ??
          latestMeasurement?.biologicalSex ??
          null,
        dateOfBirth:
          savedProfileOverride?.dateOfBirth ??
          fromPatient?.dateOfBirth ??
          latestMeasurement?.dateOfBirth ??
          null,
        heightCm:
          savedProfileOverride?.heightCm ??
          fromPatient?.heightCm ??
          latestMeasurement?.heightCm ??
          null,
      });
    }, [
      latestMeasurement?.biologicalSex,
      latestMeasurement?.dateOfBirth,
      latestMeasurement?.heightCm,
      patientQuery.data?.profile,
      savedProfileOverride,
    ]);

    const comparisonsQuery = useQuery({
      queryKey: ['body-image', 'comparisons', session.id],
      queryFn: () => fetchBodyImageComparisons(session.id),
    });

    const trackingEventsQuery = useQuery({
      queryKey: ['body-image', 'tracking-events', session.id],
      queryFn: () => fetchBodyImageTrackingEvents(session.id),
    });

    const isBaseProfileComplete =
      isPatientBaseProfileComplete(registeredProfile);
    const missingBaseProfileLabels =
      getMissingPatientBaseProfileLabels(registeredProfile);
    const biologicalSex = registeredProfile.biologicalSex;
    const latestSnapshot = useMemo(
      () => extractBodyImageMeasurementSnapshot(latestMeasurement),
      [latestMeasurement]
    );
    const measurementFormInitialValues = useMemo(
      () =>
        createMeasurementPrefillFromLatest(
          session.patient_id,
          latestMeasurement,
          registeredProfile
        ),
      [latestMeasurement, registeredProfile, session.patient_id]
    );
    const latestMeasurementFields = useMemo(() => {
      if (latestMeasurement == null) {
        return [];
      }

      return latestMeasurement.fields.filter(
        field => field.value.trim() !== ''
      );
    }, [latestMeasurement]);
    const hasMeasurementValues = latestMeasurementFields.length > 0;

    const hasSessionSensor = useMemo(() => {
      if (session.sensor_id != null) {
        return true;
      }

      if (session.connectedTo != null && session.connectedTo !== '') {
        return true;
      }

      return (session.connectedDevices ?? []).some(
        device => device.type === 'smartwatch' || device.type === 'heart-rate'
      );
    }, [session.sensor_id, session.connectedTo, session.connectedDevices]);
    const isSessionRunning = session.status !== 'queued';
    const isMeasurementConfirmedEffective =
      isSessionRunning || isMeasurementConfirmed;

    const deviceState = useMemo(
      () =>
        allDevices.map(device => {
          const clientId = device.clientId ?? device.code;
          return {
            ...device,
            status:
              connectionStatus[clientId ?? ''] ??
              device.status ??
              'disconnected',
          };
        }),
      [allDevices, connectionStatus]
    );
    const vrStatus = getDeviceStatus(deviceState, 'vr-headset');
    const isVrConnected =
      vrStatus === 'connected' || vrStatus === 'receiving_data';
    const isSensorConnected =
      getDeviceStatus(deviceState, 'smartwatch') === 'connected';
    const requiredDevicesConnected =
      (!appRequirements.vr || isVrConnected) &&
      (!appRequirements.sensor || isSensorConnected);

    const previousMeasurementIdRef = useRef<number | null | undefined>(
      undefined
    );
    const previousSessionIdRef = useRef(session.id);

    useEffect(() => {
      if (previousSessionIdRef.current !== session.id) {
        previousSessionIdRef.current = session.id;
        previousMeasurementIdRef.current = undefined;
      }

      if (session.status !== 'queued') {
        setIsMeasurementConfirmed(true);
        previousMeasurementIdRef.current = latestMeasurement?.id ?? null;
        return;
      }

      const currentId = latestMeasurement?.id ?? null;
      const previousId = previousMeasurementIdRef.current;

      // First observation for this session: remember id, keep unconfirmed.
      if (previousId === undefined) {
        previousMeasurementIdRef.current = currentId;
        setIsMeasurementConfirmed(false);
        return;
      }

      // Only clear confirmation when the active measurement changes.
      if (previousId !== currentId) {
        previousMeasurementIdRef.current = currentId;
        setIsMeasurementConfirmed(false);
      }
    }, [session.id, session.status, latestMeasurement?.id]);

    const canStartSession =
      isBaseProfileComplete &&
      latestMeasurement != null &&
      hasMeasurementValues &&
      isMeasurementConfirmed &&
      requiredDevicesConnected;

    const startDisabledReason = !isBaseProfileComplete
      ? `Completa i dati utente mancanti: ${missingBaseProfileLabels.join(', ')}.`
      : latestMeasurement == null || !hasMeasurementValues
        ? 'Registra una rilevazione body completa.'
        : !isMeasurementConfirmed
          ? 'Conferma l’ultima rilevazione body prima di avviare.'
          : !requiredDevicesConnected
            ? appRequirements.vr && !isVrConnected
              ? 'Connetti il visore VR richiesto.'
              : 'Connetti i dispositivi obbligatori.'
            : null;

    const startButtonLabel = (() => {
      const base =
        selectedFigure != null
          ? `Avvia sessione (figura ${selectedFigure})`
          : 'Avvia sessione';

      if (canStartSession || isStartingSession || isPending) {
        return base;
      }

      if (
        latestMeasurement != null &&
        hasMeasurementValues &&
        isBaseProfileComplete &&
        !isMeasurementConfirmed
      ) {
        return 'Avvia sessione (in attesa di conferma rilevazione)';
      }

      if (appRequirements.vr && !isVrConnected) {
        return 'Avvia sessione (in attesa del Visore)';
      }

      return base;
    })();

    const invalidateBodyImageQueries = async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['body-image', 'comparisons', session.id],
        }),
        queryClient.invalidateQueries({
          queryKey: ['body-image', 'tracking-events', session.id],
        }),
      ]);
    };

    const trackingEventsQueryKey = [
      'body-image',
      'tracking-events',
      session.id,
    ] as const;

    const trackingMutation = useMutation({
      mutationFn: (values: {
        eventName: BodyImageEventName | string;
        eventPayload: Record<string, unknown>;
      }) =>
        createBodyImageTrackingEvent(session.id, {
          patientId: session.patient_id,
          eventName: values.eventName,
          eventPayload: values.eventPayload,
        }),
      onSuccess: savedEvent => {
        queryClient.setQueryData<BodyImageTrackingEvent[]>(
          trackingEventsQueryKey,
          previous => {
            const list = previous ?? [];
            if (list.some(event => event.id === savedEvent.id)) {
              return list;
            }
            return [savedEvent, ...list];
          }
        );
      },
    });

    const comparisonMutation = useMutation({
      mutationFn: (draft: ComparisonDraftValues) => {
        const parameters = getComparisonParameterValues(draft.values);
        const metrics = calculateBodyImageComparisonMetrics(
          latestSnapshot,
          parameters
        );

        return createBodyImageComparison(session.id, {
          patientId: session.patient_id,
          label: draft.label.trim(),
          notes: draft.notes.trim() || null,
          parameters,
          baselineMeasurements: latestSnapshot,
          metrics,
        });
      },
      onSuccess: async () => {
        await invalidateBodyImageQueries();
        closeComparisonModal();
        setComparisonDraft(createEmptyComparisonDraft(latestSnapshot));
      },
    });

    const deleteComparisonMutation = useMutation({
      mutationFn: (comparisonId: number) =>
        deleteBodyImageComparison(session.id, comparisonId),
      onSuccess: invalidateBodyImageQueries,
    });

    const createMeasurementMutation = useMutation({
      mutationFn: (values: PatientMeasurementRecord) =>
        createPatientMeasurement(session.patient_id, values),
      onSuccess: async (data, values) => {
        const bodyImageKey = [
          'body-image',
          'patient-measurements',
          session.patient_id,
        ] as const;
        const patientProfileKey = [
          'body-image',
          'patient-profile',
          session.patient_id,
        ] as const;
        const patientsKey = patientsQueryKeys.measurementsHistory(
          session.patient_id
        );

        const nextProfile: PatientBaseProfile = {
          biologicalSex: values.biologicalSex ?? null,
          dateOfBirth: values.dateOfBirth ?? null,
          heightCm: values.heightCm ?? null,
        };

        // Local overrides survive query refetch / focus (GET patient lacks profile).
        const measurementWithProfile: PatientMeasurementRecord = {
          ...data,
          biologicalSex: values.biologicalSex ?? data.biologicalSex ?? null,
          dateOfBirth: values.dateOfBirth ?? data.dateOfBirth ?? null,
          heightCm: values.heightCm ?? data.heightCm ?? null,
        };
        setSavedMeasurementOverride(measurementWithProfile);
        setSavedProfileOverride(nextProfile);

        await queryClient.cancelQueries({ queryKey: patientProfileKey });
        await queryClient.cancelQueries({ queryKey: bodyImageKey });

        const currentBodyImageHistory =
          queryClient.getQueryData<PatientMeasurementRecord[]>(bodyImageKey) ??
          [];
        queryClient.setQueryData(bodyImageKey, [
          measurementWithProfile,
          ...currentBodyImageHistory.filter(
            item => item.id !== measurementWithProfile.id
          ),
        ]);

        const currentPatientsHistory =
          queryClient.getQueryData<PatientMeasurementRecord[]>(patientsKey) ??
          [];
        queryClient.setQueryData(patientsKey, [
          measurementWithProfile,
          ...currentPatientsHistory.filter(
            item => item.id !== measurementWithProfile.id
          ),
        ]);

        queryClient.setQueryData(
          patientProfileKey,
          (current: PatientRecord | undefined) => {
            if (current == null) {
              return {
                id: session.patient_id,
                userId: null,
                code: `Utente ${String(session.patient_id).padStart(4, '0')}`,
                profile: nextProfile,
              } satisfies PatientRecord;
            }

            return {
              ...current,
              profile: {
                biologicalSex:
                  nextProfile.biologicalSex ??
                  current.profile?.biologicalSex ??
                  null,
                dateOfBirth:
                  nextProfile.dateOfBirth ??
                  current.profile?.dateOfBirth ??
                  null,
                heightCm:
                  nextProfile.heightCm ?? current.profile?.heightCm ?? null,
              },
            };
          }
        );

        // Avoid immediate refetch: staging GET patient has no profile fields, and a
        // racing measurements refetch can wipe optimistic UI before paint.
        void queryClient.invalidateQueries({
          queryKey: patientsKey,
          refetchType: 'none',
        });

        closeMeasurementModal();
        notifications.show({
          color: 'teal',
          title: 'Rilevazione salvata',
          message: 'La nuova rilevazione è stata registrata.',
        });
      },
      onError: error => {
        notifications.show({
          color: 'red',
          title: 'Errore',
          message:
            error instanceof Error
              ? error.message
              : 'Impossibile salvare la rilevazione',
        });
      },
    });

    const appendLocalTrackingEvent = (
      eventName: BodyImageEventName | string,
      eventPayload: Record<string, unknown>
    ) => {
      const localEvent: BodyImageTrackingEvent = {
        id: -Date.now(),
        sessionId: session.id,
        patientId: session.patient_id,
        eventName,
        direction: 'outbound',
        eventPayload,
        createdAt: new Date().toISOString(),
      };

      setLocalTrackingEvents(previous => [localEvent, ...previous]);
      return localEvent;
    };

    const sendTrackedBodyImageEvent = async (
      eventName: BodyImageEventName | string,
      eventPayload: Record<string, unknown>,
      options?: { notify?: boolean }
    ) => {
      const shouldNotify = options?.notify !== false;
      console.info('[body-image] outgoing event', {
        sessionId: session.id,
        patientId: session.patient_id,
        eventName,
        eventPayload,
      });
      sendMessage(eventName, eventPayload);
      appendLocalTrackingEvent(eventName, eventPayload);

      if (shouldNotify) {
        notifications.show({
          color: 'green',
          title: 'Evento inviato',
          message: formatBodyImageEventLabel(eventName, eventPayload),
        });
      }

      try {
        const savedEvent = await trackingMutation.mutateAsync({
          eventName,
          eventPayload,
        });
        setLocalTrackingEvents(previous => {
          let removedPlaceholder = false;

          return previous.filter(event => {
            if (
              !removedPlaceholder &&
              event.id < 0 &&
              event.eventName === savedEvent.eventName
            ) {
              removedPlaceholder = true;
              return false;
            }

            return event.id !== savedEvent.id;
          });
        });
      } catch (error) {
        console.error('[body-image] tracking persistence failed', {
          sessionId: session.id,
          patientId: session.patient_id,
          eventName,
          eventPayload,
          error,
          message: error instanceof Error ? error.message : String(error),
        });
        if (shouldNotify) {
          notifications.show({
            color: 'red',
            title: 'Tracking non salvato',
            message:
              error instanceof Error
                ? error.message
                : 'Il backend non ha salvato il tracking event.',
          });
        }
      }
    };

    const handleSelectFigure = (figure: number) => {
      setSelectedFigure(figure);
      void persistSelectedFigure(figure);

      // In queued: solo persistenza DB (+ stato locale). Allo Start si invia sul device.
      // In progress: pubblica subito sul device.
      if (session.status === 'in-progress') {
        void sendTrackedBodyImageEvent(
          BODY_IMAGE_MANNEQUIN_EVENT_NAME,
          buildBodyImageMannequinSelectPayload(session, figure)
        );
      }
    };

    const handleCancelSession = async () => {
      try {
        await deleteAllBodyImageTrackingEvents(session.id);
      } catch (error) {
        console.error(
          '[body-image] failed to delete tracking events on cancel',
          {
            sessionId: session.id,
            error,
            message: error instanceof Error ? error.message : String(error),
          }
        );
        notifications.show({
          color: 'red',
          title: 'Delete tracking fallito',
          message:
            error instanceof Error
              ? error.message
              : 'Il backend non ha eliminato i tracking events.',
        });
      }

      setLocalTrackingEvents([]);
      setSelectedFigure(null);
      setRotationAngle(0);
      void persistSelectedFigure(null).catch(() => undefined);
      queryClient.setQueryData(
        ['body-image', 'tracking-events', session.id],
        []
      );
      await cancelSession();
    };

    const handleStartSessionClick = () => {
      if (!isBaseProfileComplete) {
        notifications.show({
          color: 'yellow',
          title: 'Dati utente mancanti',
          message:
            'Registra sesso, anno di nascita e altezza prima di avviare la sessione.',
        });
        return;
      }

      if (latestMeasurement == null || !hasMeasurementValues) {
        notifications.show({
          color: 'yellow',
          title: 'Rilevazione mancante',
          message:
            'Registra una rilevazione body prima di avviare la sessione.',
        });
        return;
      }

      if (!isMeasurementConfirmed) {
        notifications.show({
          color: 'yellow',
          title: 'Rilevazione non confermata',
          message:
            'Conferma l’ultima rilevazione body prima di avviare la sessione.',
        });
        setWorkspaceTab('rilevazione');
        return;
      }

      if (!requiredDevicesConnected) {
        notifications.show({
          color: 'yellow',
          title: 'Dispositivi non connessi',
          message:
            appRequirements.vr && !isVrConnected
              ? 'Connetti il visore VR richiesto prima di avviare la sessione.'
              : 'Connetti i dispositivi obbligatori prima di avviare la sessione.',
        });
        return;
      }

      openStartConfirmModal();
    };

    const handleStartConfirmModalUpdate = () => {
      closeStartConfirmModal();
      setWorkspaceTab('rilevazione');
    };

    const isSessionStarting = isStartingSession || isPending;

    const proceedStartSession = async () => {
      if (latestMeasurement == null || isSessionStarting) {
        return;
      }

      const gender = resolveBodyImageBiologicalSex(
        biologicalSex,
        latestMeasurement
      );
      if (gender == null) {
        notifications.show({
          color: 'red',
          title: 'Sesso biologico mancante',
          message:
            'Imposta male/female sul paziente selezionato prima di avviare la sessione.',
        });
        return;
      }

      setIsStartingSession(true);
      setIsMeasurementConfirmed(true);

      try {
        void sendTrackedBodyImageEvent(
          'start_bodyimage',
          buildBodyImageStartPayload(session, latestMeasurement, gender),
          { notify: false }
        );

        if (selectedFigure != null) {
          void sendTrackedBodyImageEvent(
            BODY_IMAGE_MANNEQUIN_EVENT_NAME,
            buildBodyImageMannequinSelectPayload(session, selectedFigure),
            { notify: false }
          );
        }

        await startExperience({
          skipDeviceStart: true,
          persistInBackground: true,
        });

        closeStartConfirmModal();
        setWorkspaceTab('controllo-eventi');
      } finally {
        setIsStartingSession(false);
      }
    };

    const handleCreateComparison = async () => {
      const name = comparisonDraft.label.trim();
      if (name === '') {
        notifications.show({
          color: 'yellow',
          title: 'Nome mancante',
          message: 'Inserisci un nome per il nuovo manichino custom.',
        });
        return;
      }

      const parameters = getComparisonParameterValues(comparisonDraft.values);
      if (Object.keys(parameters).length === 0) {
        notifications.show({
          color: 'yellow',
          title: 'Parametri mancanti',
          message:
            'Inserisci almeno una misura (cm/kg) per il manichino custom.',
        });
        return;
      }

      try {
        await comparisonMutation.mutateAsync({
          ...comparisonDraft,
          label: name,
        });
      } catch (error) {
        notifications.show({
          color: 'red',
          title: 'Manichino non creato',
          message:
            error instanceof Error
              ? error.message
              : 'Impossibile salvare il manichino custom.',
        });
      }
    };

    const handleBodyImageRotate = (direction: -1 | 1) => {
      const nextAngle = Math.max(
        -360,
        Math.min(360, rotationAngle + direction * rotateStep)
      );
      setRotationAngle(nextAngle);
      void sendTrackedBodyImageEvent(
        BODY_IMAGE_ROTATE_EVENT_NAME,
        buildBodyImageMannequinsRotatePayload(session, nextAngle)
      );
    };

    const handleOpenComparisonModal = () => {
      setComparisonDraft(createEmptyComparisonDraft(latestSnapshot));
      openComparisonModal();
    };

    const isControlPanelDisabled =
      session.status !== 'in-progress' || trackingMutation.isPending;
    const canSelectFigureBeforeStart = session.status === 'queued';
    const isFigureSelectDisabled =
      trackingMutation.isPending ||
      (session.status !== 'queued' && session.status !== 'in-progress');

    const comparisons = comparisonsQuery.data ?? [];
    const isTrackingEventsLoading = trackingEventsQuery.isLoading;
    const isComparisonsLoading = comparisonsQuery.isLoading;

    const handleDeleteComparison = (comparison: BodyImageComparison) => {
      if (activeComparedComparisonId === comparison.id) {
        setActiveComparedComparisonId(null);
      }

      void deleteComparisonMutation.mutateAsync(comparison.id);
    };

    const handleShowComparisonMannequin = (comparison: BodyImageComparison) => {
      if (selectedFigure == null) {
        return;
      }

      setActiveComparedComparisonId(comparison.id);

      void sendTrackedBodyImageEvent(
        'bodyImage:mannequins:compare',
        buildBodyImageMannequinsComparePayload(session, selectedFigure, {
          id: comparison.id,
          label: comparison.label,
          parameters: comparison.parameters,
        })
      );
    };

    const comparisonControlAccordionProps = {
      comparisons,
      activeComparisonId: activeComparedComparisonId,
      isDeleteDisabled: session.status !== 'in-progress',
      isDeletePending: deleteComparisonMutation.isPending,
      onDelete: handleDeleteComparison,
      showDeviceAction: true,
      isDeviceActionDisabled: isControlPanelDisabled || selectedFigure == null,
      onShowMannequin: handleShowComparisonMannequin,
    } as const;

    const trackingEvents = useMemo(() => {
      const persistedEvents = trackingEventsQuery.data ?? [];
      const merged = [...localTrackingEvents, ...persistedEvents];
      const seenIds = new Set<number>();

      return merged.filter(event => {
        if (seenIds.has(event.id)) {
          return false;
        }

        seenIds.add(event.id);
        return true;
      });
    }, [localTrackingEvents, trackingEventsQuery.data]);

    const trackingEventsPanel = (
      <Card withBorder p='md' radius='sm'>
        <Stack gap='sm'>
          <Group justify='space-between' align='center'>
            <Text fw={600}>Tracking eventi inviati</Text>
            {isTrackingEventsLoading ? (
              <Badge variant='light' color='gray'>
                Caricamento
              </Badge>
            ) : null}
          </Group>
          {isTrackingEventsLoading ? (
            <TrackingTableSkeleton />
          ) : trackingEvents.length === 0 ? (
            <Text size='sm' c='dimmed'>
              Nessun evento Body Image inviato finora.
            </Text>
          ) : (
            <Table withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Evento</Table.Th>
                  <Table.Th>Quando</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {trackingEvents.map(event => (
                  <Table.Tr key={event.id}>
                    <Table.Td>
                      {formatBodyImageEventLabel(
                        event.eventName,
                        event.eventPayload
                      )}
                      {event.id < 0 && (
                        <Text component='span' size='xs' c='dimmed'>
                          {' '}
                          (non salvato)
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>{event.createdAt ?? '-'}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Stack>
      </Card>
    );

    const sessionBox = (
      <Stack gap='sm'>
        <SessionInfoCard
          patientCode={session.patientCode}
          patientId={session.patient_id}
          appType={session.type}
          scenario={session.scenario}
          videoName={session.video?.name}
          source={session.video?.category ?? session.video?.product_name}
          isLoading={isLoading}
        />

        {isDebugMode ? <SessionConfigSummary session={session} /> : null}

        <Card withBorder p='md' radius='sm'>
          <Stack gap='sm'>
            <Group justify='space-between' align='center'>
              <Text fw={600}>Stato sessione</Text>
              <Badge
                color={statusColorByState[session.status] ?? 'gray'}
                variant='light'
              >
                {session.status}
              </Badge>
            </Group>

            {isSessionStarting && (
              <Group gap='xs'>
                <Loader size='sm' />
                <Text size='sm' c='dimmed'>
                  Avvio sessione in corso…
                </Text>
              </Group>
            )}

            {session.status === 'in-progress' && !isSessionStarting && (
              <SessionTimer session={session} />
            )}

            {session.status === 'queued' && !isSessionStarting && (
              <Text size='sm' c='dimmed'>
                La sessione parte solo dopo la conferma dell’ultima rilevazione
                body dell'utente.
                {selectedFigure != null
                  ? ` All’avvio verrà presentata solo la figura ${selectedFigure}.`
                  : ''}
              </Text>
            )}

            <Group grow>
              {session.status === 'queued' ? (
                <Tooltip
                  label={startDisabledReason ?? ''}
                  disabled={startDisabledReason == null || isSessionStarting}
                  multiline
                  w={280}
                >
                  <Box style={{ flex: 1 }}>
                    <Button
                      fullWidth
                      leftSection={<Play size={16} />}
                      onClick={handleStartSessionClick}
                      loading={isSessionStarting}
                      disabled={isSessionStarting || !canStartSession}
                    >
                      {startButtonLabel}
                    </Button>
                  </Box>
                </Tooltip>
              ) : session.status === 'in-progress' ? (
                <>
                  <Button
                    color='teal'
                    leftSection={<Check size={16} />}
                    loading={isPending}
                    onClick={() => void terminateSession()}
                  >
                    Completa e termina
                  </Button>
                  <Button
                    variant='light'
                    color='gray'
                    leftSection={<X size={16} />}
                    onClick={() => void handleCancelSession()}
                  >
                    Annulla
                  </Button>
                </>
              ) : null}
            </Group>
          </Stack>
        </Card>
      </Stack>
    );

    const devicesBox = (
      <ConnectedDevices
        onConfigureClick={onConfigureClick}
        vrRequired={appRequirements.vr}
        sensorRequired={false}
        devices={allDevices}
        session={session}
      />
    );

    const workspaceBox = (
      <Tabs value={workspaceTab} onChange={setWorkspaceTab}>
        <Tabs.List>
          <Tabs.Tab value='rilevazione' leftSection={<Ruler size={16} />}>
            Rilevazione body
          </Tabs.Tab>
          <Tabs.Tab
            value='controllo-eventi'
            leftSection={<SlidersHorizontal size={16} />}
          >
            Controllo eventi
          </Tabs.Tab>
          <Tabs.Tab value='tracking-eventi' leftSection={<History size={16} />}>
            Tracking eventi
          </Tabs.Tab>
          {hasSessionSensor ? (
            <Tabs.Tab
              value='eventi'
              ml='auto'
              leftSection={<HeartPulse size={16} />}
            >
              Eventi Sensore HR
            </Tabs.Tab>
          ) : null}
        </Tabs.List>

        <Tabs.Panel value='rilevazione' pt='md'>
          <Card withBorder p='md' radius='sm'>
            {measurementsQuery.isLoading ? (
              <ActiveMeasurementPanelSkeleton />
            ) : (
              <Stack gap='sm'>
                <Paper withBorder p='md' radius='sm'>
                  <Stack gap='sm'>
                    <Group
                      justify='space-between'
                      align='flex-start'
                      wrap='wrap'
                    >
                      <Stack gap={4}>
                        <Text fw={600}>Dati utente</Text>
                        <Text size='sm' c='dimmed'>
                          Sesso, anno di nascita e altezza si modificano solo
                          dalla pagina utente.
                        </Text>
                      </Stack>
                      <Anchor
                        component={Link}
                        to={USER_ROUTES.detail(session.patient_id)}
                        size='sm'
                      >
                        Apri pagina utente
                      </Anchor>
                    </Group>

                    <Grid>
                      <Grid.Col span={{ base: 12, sm: 4 }}>
                        <Text size='sm' c='dimmed'>
                          Sesso biologico
                        </Text>
                        <Text fw={600}>
                          {formatBiologicalSex(registeredProfile.biologicalSex)}
                        </Text>
                      </Grid.Col>
                      <Grid.Col span={{ base: 12, sm: 4 }}>
                        <Text size='sm' c='dimmed'>
                          Anno di nascita
                        </Text>
                        <Text fw={600}>
                          {formatBirthYearValue(registeredProfile.dateOfBirth)}
                        </Text>
                        <Text size='sm' c='dimmed'>
                          {formatAgeValue(
                            deriveAgeFromDateOfBirth(
                              registeredProfile.dateOfBirth
                            )
                          )}
                        </Text>
                      </Grid.Col>
                      <Grid.Col span={{ base: 12, sm: 4 }}>
                        <Text size='sm' c='dimmed'>
                          Altezza
                        </Text>
                        <Text fw={600}>
                          {formatMeasurementValue(
                            registeredProfile.heightCm,
                            'cm'
                          )}
                        </Text>
                      </Grid.Col>
                    </Grid>

                    {session.status === 'queued' && !isBaseProfileComplete ? (
                      <Alert color='yellow' title='Dati obbligatori mancanti'>
                        Prima di avviare la sessione registra{' '}
                        {missingBaseProfileLabels.join(', ')} nel form
                        rilevazione.
                      </Alert>
                    ) : null}
                  </Stack>
                </Paper>

                <Paper withBorder p='md' radius='sm'>
                  <Stack gap='sm'>
                    <Group
                      justify='space-between'
                      align='flex-start'
                      wrap='wrap'
                      gap='sm'
                    >
                      <Stack gap={2}>
                        <Text fw={600}>Ultima rilevazione</Text>
                        {latestMeasurement != null ? (
                          <Text size='sm' c='dimmed'>
                            {formatDateTimeValue(
                              latestMeasurement.createdAt ??
                                latestMeasurement.updatedAt
                            )}
                          </Text>
                        ) : null}
                      </Stack>
                      <Group gap='xs' wrap='wrap' justify='flex-end'>
                        {!hasMeasurementValues || !isBaseProfileComplete ? (
                          <Button
                            variant='filled'
                            onClick={openMeasurementModal}
                          >
                            {!hasMeasurementValues
                              ? 'Inserisci rilevazione'
                              : 'Completa dati obbligatori'}
                          </Button>
                        ) : (
                          <>
                            <Button
                              leftSection={<Check size={16} />}
                              variant='filled'
                              color={
                                isMeasurementConfirmedEffective
                                  ? 'teal'
                                  : undefined
                              }
                              disabled={isSessionRunning}
                              onClick={() => setIsMeasurementConfirmed(true)}
                            >
                              {isMeasurementConfirmedEffective
                                ? 'Confermato'
                                : 'Conferma rilevazione'}
                            </Button>
                            <Button
                              variant='outline'
                              onClick={openMeasurementModal}
                            >
                              Aggiorna valori
                            </Button>
                          </>
                        )}
                      </Group>
                    </Group>

                    {latestMeasurement == null ? (
                      <Text size='sm' c='dimmed'>
                        Nessuna rilevazione disponibile per questo utente.
                      </Text>
                    ) : (
                      <Stack gap='md'>
                        {latestMeasurement.fields.length === 0 ? (
                          <Text size='sm' c='dimmed'>
                            Nessun campo registrato in questa rilevazione.
                          </Text>
                        ) : (
                          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing='sm'>
                            {latestMeasurement.fields.map(field => (
                              <PatientMeasurementFieldTile
                                key={String(
                                  field.id ??
                                    `${field.label}-${field.metricType}`
                                )}
                                field={field}
                              />
                            ))}
                          </SimpleGrid>
                        )}

                        <Paper withBorder p='md' radius='sm'>
                          <Stack gap='xs'>
                            <Text fw={600}>Note rilevazione corrente</Text>
                            <Text size='sm' c='dimmed'>
                              {latestMeasurement.notes.trim() || 'Nessuna nota'}
                            </Text>
                          </Stack>
                        </Paper>
                      </Stack>
                    )}
                  </Stack>
                </Paper>

                <Group justify='center' mt='xs'>
                  <Anchor
                    component={Link}
                    to={USER_ROUTES.bodyTab(session.patient_id)}
                    size='sm'
                  >
                    Vai alle rilevazioni
                  </Anchor>
                </Group>
              </Stack>
            )}
          </Card>
        </Tabs.Panel>

        <Tabs.Panel value='controllo-eventi' pt='md'>
          <Stack gap='md'>
            <Card withBorder p={0} radius='sm'>
              <Grid gutter={0} align='stretch'>
                <Grid.Col span={{ base: 12, sm: 6 }} p='sm' bg='gray.0'>
                  <Stack h='100%' justify='space-between'>
                    <BodyImageRotationCard
                      title='Rotazione'
                      angle={rotationAngle}
                      disabled={isControlPanelDisabled}
                      layout='vertical'
                      embedded
                      onRotateLeft={() => handleBodyImageRotate(-1)}
                      onRotateRight={() => handleBodyImageRotate(1)}
                      onReset={() => {
                        setRotationAngle(0);
                        void sendTrackedBodyImageEvent(
                          BODY_IMAGE_ROTATE_EVENT_NAME,
                          buildBodyImageMannequinsRotatePayload(session, 0)
                        );
                      }}
                    />
                  </Stack>
                </Grid.Col>

                <Grid.Col span={12} hiddenFrom='sm' p={0}>
                  <Divider />
                </Grid.Col>

                <Grid.Col span={{ base: 12, sm: 6 }} p='sm'>
                  <Stack gap='sm' h='100%' justify='space-between'>
                    <Stack gap='sm' w='100%'>
                      <Group
                        justify='space-between'
                        align='center'
                        gap='xs'
                        wrap='nowrap'
                      >
                        <Tooltip
                          withArrow
                          multiline
                          w={240}
                          label={
                            canSelectFigureBeforeStart
                              ? 'Pre-seleziona una figura: allo Start verrà mostrata solo quella sul device.'
                              : 'Scegli la figura da mostrare sul device.'
                          }
                        >
                          <Text
                            size='sm'
                            fw={600}
                            lh={1.2}
                            style={{ cursor: 'help' }}
                          >
                            Selezione manichino
                          </Text>
                        </Tooltip>
                        <Badge
                          variant='light'
                          color={selectedFigure != null ? 'blue' : 'gray'}
                          size='sm'
                          radius='sm'
                        >
                          {selectedFigure != null
                            ? `Fig. ${selectedFigure}`
                            : '—'}
                        </Badge>
                      </Group>

                      <SimpleGrid
                        cols={3}
                        spacing={6}
                        w='100%'
                        maw={262}
                        mx='auto'
                      >
                        {Array.from({
                          length: BODY_IMAGE_FIGURE_COUNT,
                        }).map((_, index) => {
                          const figure = index + 1;
                          const isSelected = selectedFigure === figure;

                          return (
                            <UnstyledButton
                              key={`select-${figure}`}
                              disabled={isFigureSelectDisabled}
                              onClick={() => handleSelectFigure(figure)}
                              aria-pressed={isSelected}
                              aria-label={`Seleziona figura ${figure}`}
                              style={{
                                display: 'block',
                                width: '100%',
                                opacity: isFigureSelectDisabled ? 0.45 : 1,
                                cursor: isFigureSelectDisabled
                                  ? 'not-allowed'
                                  : 'pointer',
                              }}
                            >
                              <Box
                                w='100%'
                                style={{
                                  position: 'relative',
                                  aspectRatio: '1',
                                  borderRadius: 6,
                                  overflow: 'hidden',
                                  border: isSelected
                                    ? '2px solid var(--mantine-color-blue-5)'
                                    : '1px solid var(--mantine-color-gray-4)',
                                  backgroundColor: isSelected
                                    ? 'var(--mantine-color-blue-0)'
                                    : '#F1F3F5',
                                  boxShadow: isSelected
                                    ? '0 0 0 1px var(--mantine-color-blue-1)'
                                    : undefined,
                                  transition:
                                    'border-color 100ms ease, background-color 100ms ease',
                                }}
                              >
                                <Image
                                  src={getBodyImageFigurePlaceholderSrc(figure)}
                                  alt={`Figura ${figure}`}
                                  fit='cover'
                                  w='100%'
                                  h='100%'
                                />
                                <Text
                                  pos='absolute'
                                  top='50%'
                                  left='50%'
                                  fw={600}
                                  lh={1}
                                  fz='xl'
                                  c={isSelected ? 'blue.8' : 'gray.7'}
                                  style={{
                                    transform: 'translate(-50%, -50%)',
                                    pointerEvents: 'none',
                                    textShadow:
                                      '0 0 10px rgba(255,255,255,0.95), 0 1px 2px rgba(0,0,0,0.12)',
                                  }}
                                >
                                  {figure}
                                </Text>
                              </Box>
                            </UnstyledButton>
                          );
                        })}
                      </SimpleGrid>
                    </Stack>

                    <Button
                      fullWidth
                      disabled={isControlPanelDisabled}
                      onClick={() => {
                        setActiveComparedComparisonId(null);
                        void sendTrackedBodyImageEvent(
                          'bodyImage:mannequins:home',
                          buildBodyImageMannequinsHomePayload(session)
                        );
                      }}
                    >
                      Mostra tutti i manichini
                    </Button>
                  </Stack>
                </Grid.Col>
              </Grid>
            </Card>

            <Card withBorder p='md' radius='sm'>
              <Stack gap='sm'>
                <Group
                  justify='space-between'
                  align='flex-start'
                  wrap='wrap'
                  gap='sm'
                >
                  <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                    <Text fw={600}>
                      Manichini personalizzati di comparazione
                    </Text>
                    <Text size='sm' c='dimmed'>
                      Manichini creati da misure custom per affiancarli alla
                      figura selezionata.
                      {selectedFigure != null
                        ? ` Figura attiva: ${selectedFigure}.`
                        : ' Seleziona una figura sopra per abilitare Mostra manichino.'}
                    </Text>
                  </Stack>
                  <Button
                    leftSection={<Plus size={16} />}
                    variant='filled'
                    onClick={handleOpenComparisonModal}
                    disabled={session.status !== 'in-progress'}
                  >
                    Aggiungi nuovo
                  </Button>
                </Group>
                {isComparisonsLoading ? (
                  <SectionSkeletonList items={2} />
                ) : comparisons.length === 0 ? (
                  <Text size='sm' c='dimmed'>
                    Nessun manichino personalizzato. Usa &ldquo;Aggiungi
                    nuovo&rdquo; per crearne uno.
                  </Text>
                ) : (
                  <BodyImageComparisonAccordionList
                    {...comparisonControlAccordionProps}
                    showPreview
                  />
                )}
              </Stack>
            </Card>
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value='tracking-eventi' pt='md'>
          {trackingEventsPanel}
        </Tabs.Panel>

        {hasSessionSensor ? (
          <Tabs.Panel value='eventi' pt='md'>
            <SessionCharts session={session} />
          </Tabs.Panel>
        ) : null}
      </Tabs>
    );

    return (
      <>
        <Grid gutter='md'>
          <Grid.Col span={{ base: 12, lg: 4 }}>
            <Stack gap='md'>
              {sessionBox}
              {devicesBox}
            </Stack>
          </Grid.Col>
          <Grid.Col span={{ base: 12, lg: 8 }}>{workspaceBox}</Grid.Col>
        </Grid>

        <Modal
          opened={startConfirmModalOpened}
          onClose={closeStartConfirmModal}
          title='Conferma rilevazione per l’avvio'
          centered
        >
          <Stack gap='md'>
            <Text size='sm'>
              L&apos;ultima rilevazione è del{' '}
              <Text span fw={600}>
                {formatDateValue(latestMeasurementRecordedAt)}
              </Text>
              {latestMeasurementDaysAgo != null ? (
                <> ({latestMeasurementDaysAgo})</>
              ) : null}
              . Confermi di utilizzare questa?
            </Text>
            <Group justify='flex-end' gap='xs'>
              <Button variant='default' onClick={handleStartConfirmModalUpdate}>
                Aggiorna
              </Button>
              <Button
                leftSection={<Play size={16} />}
                loading={isSessionStarting}
                disabled={isSessionStarting}
                onClick={() => void proceedStartSession()}
              >
                Conferma
              </Button>
            </Group>
          </Stack>
        </Modal>

        <Modal
          opened={measurementModalOpened}
          onClose={closeMeasurementModal}
          title='Aggiorna rilevazione body'
          centered
          size='xl'
        >
          <PatientMeasurementsForm
            patientId={session.patient_id}
            initialValues={measurementFormInitialValues}
            baseProfile={registeredProfile}
            requireMissingBaseProfile
            saving={createMeasurementMutation.isPending}
            onCancel={closeMeasurementModal}
            onSubmit={async values => {
              await createMeasurementMutation.mutateAsync(values);
            }}
          />
        </Modal>

        <Modal
          opened={comparisonModalOpened}
          onClose={closeComparisonModal}
          title='Nuovo manichino custom'
          centered
        >
          <Stack gap='sm'>
            <TextInput
              label='Nome manichino'
              placeholder='Es. Obiettivo estetico 1'
              required
              value={comparisonDraft.label}
              onChange={event => {
                const value = event.currentTarget.value;
                setComparisonDraft(current => ({
                  ...current,
                  label: value,
                }));
              }}
            />

            <Textarea
              label='Note'
              placeholder='Annotazioni opzionali sul manichino…'
              minRows={2}
              value={comparisonDraft.notes}
              onChange={event => {
                const value = event.currentTarget.value;
                setComparisonDraft(current => ({
                  ...current,
                  notes: value,
                }));
              }}
            />

            <Text size='sm' c='dimmed'>
              Inserisci le misure in cm/kg. Vengono salvate e inviate ad Ably
              con gli stessi valori assoluti.
            </Text>

            <SimpleGrid cols={2} spacing='sm'>
              {BODY_IMAGE_METRIC_DEFINITIONS.map(definition => (
                <NumberInput
                  key={definition.key}
                  label={definition.label}
                  value={comparisonDraft.values[definition.key] ?? ''}
                  onChange={value =>
                    setComparisonDraft(current => ({
                      ...current,
                      values: {
                        ...current.values,
                        [definition.key]:
                          typeof value === 'number' && Number.isFinite(value)
                            ? value
                            : '',
                      },
                    }))
                  }
                />
              ))}
            </SimpleGrid>

            <Group justify='flex-end'>
              <Button variant='default' onClick={closeComparisonModal}>
                Chiudi
              </Button>
              <Button
                onClick={() => void handleCreateComparison()}
                loading={comparisonMutation.isPending}
              >
                Salva manichino
              </Button>
            </Group>
          </Stack>
        </Modal>
      </>
    );
  }
);

BodyImageScenarioLayout.displayName = 'BodyImageScenarioLayout';
